// Live GPS tracker: the trip trail plus this device's public presence.
//
// BATTERY BUDGET (NFR: at most 4 percent of the battery per day of
// tracking). watchPosition runs with enableHighAccuracy so the trail is
// actually usable, but the RADIO is what costs power, not the GPS callback.
// So fixes are buffered in memory and flushed AT MOST once every 10 seconds,
// as one batched POST /trips/:id/track through the outbox, instead of one
// request per fix. Fixes that moved less than MIN_MOVE_M since the last kept
// point are dropped before they ever reach the buffer, which keeps a phone
// sitting still on a table from filling the queue with identical rows.
//
// PRESENCE RIDES THE SAME CADENCE AND THE SAME WATCH. POST /presence is sent
// from the same flush timer as the track batch, from the same GeoProvider
// watch, so there is exactly one geolocation path in the app and the radio
// wakes once per interval, not twice. Presence is deliberately NOT trip
// gated: a traveller with no active trip still belongs on the public map.
import { apiClient } from "./api";
import { distanceM, getGeoProvider, type GeoError, type GeoFix } from "./geo";
import { enqueueTrack } from "./outbox";
import { drainOutbox } from "./sync";
import { uuid } from "./uuid";
import type { TrackPointInput } from "./types";

export const FLUSH_INTERVAL_MS = 10_000;
const MIN_MOVE_M = 8;
const MAX_BATCH = 500; // Contract cap on points per request.

export interface TrackerOptions {
  /** Null means "no active trip": presence is still published. */
  tripId: string | null;
  /** Publish this device's position to the public map. Default true. */
  publishPresence?: boolean;
  onFix?(fix: GeoFix): void;
  onError?(error: GeoError): void;
}

export function toTrackPoint(fix: GeoFix): TrackPointInput {
  return {
    lat: fix.lat,
    lng: fix.lng,
    accuracyM: fix.accuracyM,
    altitudeM: fix.altitudeM,
    speedMps: fix.speedMps,
    recordedAt: fix.recordedAt,
  };
}

export function startTracker(options: TrackerOptions): () => void {
  const publishPresence = options.publishPresence !== false;
  let buffer: TrackPointInput[] = [];
  let lastKept: GeoFix | null = null;
  let latest: GeoFix | null = null;
  let published: GeoFix | null = null;
  let stopped = false;

  async function flushTrack(): Promise<void> {
    if (!options.tripId || buffer.length === 0) return;
    const batch = buffer.slice(0, MAX_BATCH);
    buffer = buffer.slice(batch.length);
    await enqueueTrack(uuid(), options.tripId, batch);
    void drainOutbox();
  }

  async function flushPresence(): Promise<void> {
    if (!publishPresence || !latest) return;
    // Nothing moved and nothing to refresh: skip the request rather than
    // spend the radio on a duplicate.
    if (published === latest) return;
    published = latest;
    try {
      await apiClient.publishPresence({
        lat: latest.lat,
        lng: latest.lng,
        accuracyM: latest.accuracyM,
        tripId: options.tripId,
      });
    } catch {
      // Presence is best effort and expires server side. It is never queued
      // in the outbox: a stale position replayed after a reconnect would put
      // this traveller on the public map where they no longer are.
    }
  }

  const watch = getGeoProvider().watch(
    (fix) => {
      if (stopped) return;
      latest = fix;
      options.onFix?.(fix);
      // First fix goes out at once so the traveller appears on the public map
      // immediately instead of after a full interval. Every fix after that
      // waits for the batched flush.
      if (published === null) void flushPresence();
      if (lastKept && distanceM(lastKept, fix) < MIN_MOVE_M) return;
      lastKept = fix;
      buffer.push(toTrackPoint(fix));
    },
    (error) => options.onError?.(error),
  );

  const timer = setInterval(() => {
    void flushTrack();
    void flushPresence();
  }, FLUSH_INTERVAL_MS);

  return () => {
    stopped = true;
    clearInterval(timer);
    watch.stop();
    void flushTrack();
    if (publishPresence) {
      // Leave the public map cleanly instead of lingering as a ghost pin.
      void apiClient.clearPresence().catch(() => {});
    }
  };
}
